import { Link, useParams } from 'react-router-dom';
import { useT } from '@/i18n';
import { AppShell } from '@/components/layout';
import { Alert, ArrowLeft, Button, EmptyState, RoomTag, Skeleton } from '@/components/ui';
import { isRoom, type RoomKey } from '@/lib/rooms';
import { ActivityPicture, LogBar, splitTitle } from '@/features/programme/components';
import { isActive, useProgramme } from '@/features/programme/useProgramme';
import { GoalCard } from '@/features/programme/GoalCard';

/** One home-programme activity: picture, materials, steps, what to watch for, and "how did it go". */
export const Activity = () => {
  const t = useT();
  const { assessmentId = '', exerciseId = '' } = useParams();
  const state = useProgramme(assessmentId);
  const block = state.blocks.get(exerciseId);
  const back = `/programme/${assessmentId}`;

  if (state.loading) {
    return (
      <AppShell>
        <Skeleton lines={10} />
      </AppShell>
    );
  }
  if (state.error || state.locked) {
    return (
      <AppShell>
        <Alert tone="warn">{t('prog.loadFailed')}</Alert>
        <Button variant="secondary" className="mt-4" onClick={state.reload}>
          {t('prog.retry')}
        </Button>
      </AppShell>
    );
  }
  if (!block || !state.data) {
    return (
      <AppShell>
        <EmptyState
          title={t('act.notFound')}
          body={t('prog.none.body')}
          action={
            <Link to={back} className="text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
              {t('home.seeProgramme')}
            </Link>
          }
        />
      </AppShell>
    );
  }

  const room: RoomKey = isRoom(block.room) ? block.room : 'floor';
  const [title, sub] = splitTitle(block.title);
  const child = state.data.programme.child.name;

  return (
    <AppShell>
      <Link to={back} className="inline-flex min-h-tap items-center gap-1.5 rounded-md text-label font-extrabold text-lagoon-600 hover:underline">
        <ArrowLeft className="h-5 w-5" />
        {t('act.back', { room: t(`room.${room}`) })}
      </Link>

      <article className="mt-3 grid gap-8 nav:grid-cols-[1.05fr_1fr] nav:gap-10">
        <div className="flex flex-col gap-4 nav:sticky nav:top-24 nav:self-start">
          <div className="h-[240px] overflow-hidden rounded-xl bg-white shadow-1 sm:h-[340px] nav:h-[380px]">
            <ActivityPicture exerciseId={block.exerciseId} pillar={block.pillar} scene={block.scene} room={room} className="h-full w-full" />
          </div>
          <section aria-labelledby="needs">
            <h2 id="needs" className="text-label font-extrabold text-ink">{t('act.youNeed')}</h2>
            <ul className="mt-2 flex flex-wrap gap-2">
              {(block.materials.length ? block.materials : [t('act.nothing')]).map((m) => (
                <li key={m} className="rounded-md border-[1.5px] border-line bg-white px-3 py-1.5 text-label">
                  {m}
                </li>
              ))}
            </ul>
          </section>
          <section className="rounded-lg bg-sage-100 p-5 text-body text-[#2F5B40]">
            <h2 className="text-[18px] text-[#1E3F2B]">{t('act.watch')}</h2>
            <p className="mt-1">{block.successSignal}</p>
            {/* The goal card below already prints the mastery line; this is the earlier sign to step up. */}
            {block.moveOnWhen && block.moveOnWhen !== state.data.progression?.goals[block.exerciseId] && (
              <>
                <h2 className="mt-3 text-[18px] text-[#1E3F2B]">{t('act.moveOn')}</h2>
                <p className="mt-1">{block.moveOnWhen}</p>
              </>
            )}
          </section>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <RoomTag room={room} />
              <span className="text-caption text-ink-muted">
                {t('act.minutes', { n: block.durationMinutes })} · {t('act.level', { n: block.level })}
                {block.slot ? ` · ${block.slot}` : ''}
              </span>
            </div>
            <h1 className="mt-3 text-h1">{title}</h1>
            {sub && <p className="mt-1 text-body text-ink-soft">{sub}</p>}
            {!isActive(block, state.data.week) && <p className="mt-3 rounded-md bg-mist px-4 py-3 text-body text-ink-soft">{t('act.fromWeek3')}</p>}
          </div>

          <section className="rounded-lg bg-white p-5 shadow-1">
            <h2 className="text-[18px]">{t('act.helps')}</h2>
            <p className="mt-1 text-body text-ink-soft">{block.why}</p>
          </section>

          <section aria-labelledby="steps">
            <h2 id="steps" className="text-h3">{t('act.steps')}</h2>
            <ol className="mt-3 flex flex-col gap-3.5">
              {block.steps.map((step, i) => (
                <li key={i} className="grid grid-cols-[40px_1fr] items-start gap-3.5 text-[17px] leading-relaxed">
                  <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-lagoon-600 font-display text-[20px] font-extrabold text-white">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          <p className="rounded-r-lg border-l-4 border-coral-500 bg-white px-4 py-3 text-body text-ink-soft">
            <strong className="text-ink">{t('act.whenHard')}: </strong>
            {block.whenHard}
          </p>
          {block.caution && (
            <p className="rounded-md bg-attention-soft px-4 py-3 text-body text-attention-ink">
              <strong>{t('act.caution')}: </strong>
              {block.caution}
            </p>
          )}

          <LogBar exerciseId={block.exerciseId} childName={child} state={state} />
          <GoalCard key={block.exerciseId} exerciseId={block.exerciseId} assessmentId={assessmentId} state={state} />
        </div>
      </article>
    </AppShell>
  );
};
